/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
// components imports
import { Col, Grid, Descriptions, Row, Table, Card } from "antd";
// misc imports
import type { ColumnsType } from "antd/es/table";
import { IAnalytics, IArticle, IQnA } from "@/interfaces/Analytics.interface";
import { getFormattedDate } from "@/utils/date/getFormattedDate";
import { TableHiddenMessage } from "@/components/TableHiddenMessage";
import { useGetArticleById, useGetArticles } from "@/api/services";
import { CheckCircleTwoTone, CloseCircleTwoTone } from "@ant-design/icons";
import { queryClient } from "@/utils/queryClient";
import { PAGINATION_OPTIONS } from "@/constants";

const { useBreakpoint } = Grid;

/**
 * AnalyticsTable component to display analytics data in a table format.
 *
 * @component
 * @param {Object} props - Component props
 * @param {IAnalytics[] | undefined} props.analyticsData - Analytics data to be displayed
 * @param {string | undefined} props.userTimezone - User's timezone
 * @returns {JSX.Element} Rendered AnalyticsTable component
 */
export const AnalyticsTable = ({
  analyticsData,
  userTimezone,
}: {
  analyticsData: IAnalytics[] | undefined;
  userTimezone: string | undefined;
}): JSX.Element => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const { workspaceId, projectId } = useParams();

  //Parameters for API requests.
  const params = useMemo(() => {
    return {
      workspaceId: workspaceId as string,
      projectId: projectId as string,
    };
  }, [workspaceId, projectId]);
  // State to manage selected article and QnA IDs
  const [selectedArticleId, setSelectedArticleId] = useState<string>("");
  const [selectedQnAId, setSelectedQnAId] = useState<string>("");

  const QnAObject = analyticsData?.filter((item) => item.id === "qna_history");
  const isScreenBelow = (size: "sm" | "md" | "lg" | "xl" | "xxl") => screens.xs || (screens.sm && !screens[size]);

  const getArticlesById = (id: string): IArticle[] => queryClient.getQueryData(["articles", id]) || [];
  const getSelectedArticleResults = (articleId: string, qnAId: string): IArticle | undefined =>
    queryClient.getQueryData(["article", articleId, qnAId]) || undefined;

  // Fetch articles list when row is selected
  useGetArticles(
    { ...params, id: selectedQnAId },
    {
      enabled: !!selectedQnAId,
      staleTime: 3 * 60 * 1000, // 3 minutes,
    }
  );

  // Fetch article details when article row is selected
  useGetArticleById(
    { ...params, id: selectedArticleId, qna_id: selectedQnAId },
    {
      enabled: !!selectedArticleId && !!selectedQnAId, // we want both `selectedArticleId` and `selectedQnAId` are truthy values
      staleTime: 3 * 60 * 1000, // 3 minutes,
    }
  );

  // Columns configuration for the QnA table
  const columns: ColumnsType<IQnA> = [
    {
      title: t("analytics.user_message"),
      dataIndex: "user_message",
      key: "user_message",
      render: (text) => <TableHiddenMessage message={text} />,
    },
    {
      title: t("analytics.answer"),
      dataIndex: "answer",
      key: "answer",
      render: (text) => <TableHiddenMessage message={text} />,
    },
    {
      title: t("analytics.cache_hit"),
      dataIndex: "cache_hit",
      responsive: ["md"],
      key: "cache_hit",
      render: (value) => (value ? <CheckCircleTwoTone /> : <CloseCircleTwoTone />),
    },
    {
      title: t("analytics.prompt_tokens"),
      dataIndex: "prompt_tokens",
      responsive: ["md"],
      key: "prompt_tokens",
      render: (text: number, record: IQnA) => {
        return record.cache_hit ? record.hit_prompt_tokens : text;
      },
    },
    {
      title: t("analytics.completion_tokens"),
      dataIndex: "completion_tokens",
      responsive: ["md"],
      key: "completion_tokens",
      render: (text: number, record: IQnA) => {
        return record.cache_hit ? record.hit_completion_tokens : text;
      },
    },
    {
      title: t("analytics.total_tokens"),
      dataIndex: "completion_tokens",
      key: "total_tokens",
      responsive: ["lg"],
      render: (_text, record) => {
        if (record.cache_hit) {
          return <>{record.hit_completion_tokens + record.hit_prompt_tokens}</>;
        }
        return <>{record.completion_tokens + record.prompt_tokens}</>;
      },
    },
    {
      title: t("analytics.cache_read_time"),
      dataIndex: "cache_read_time",
      responsive: ["md"],
      key: "cache_read_time",
      render: (text: number, record: IQnA) => {
        return record.cache_hit ? record.hit_cache_read_time : text;
      },
    },
    {
      title: t("analytics.processing_time"),
      dataIndex: "processing_time",
      responsive: ["md"],
      key: "processing_time",
      render: (text: number, record: IQnA) => {
        return record.cache_hit ? record.hit_processing_time : text;
      },
    },
    {
      title: t("analytics.total_time"),
      dataIndex: "total_time",
      key: "total_time",
      responsive: ["lg"],
      render: (text: number, record: IQnA) => {
        return record.cache_hit ? record.hit_total_time : text;
      },
    },
    {
      title: t("analytics.created_at"),
      dataIndex: "created_at",
      key: "created_at",
      responsive: ["xl"],
      render: (text) => getFormattedDate(text, userTimezone),
    },
  ];

  return (
    <Row>
      <Col xs={24}>
        <Card title={t("analytics.qna_title")}>
          {QnAObject?.length ? (
            <Table
              size="small"
              rowKey="created_at"
              columns={columns}
              bordered
              expandable={{
                expandedRowRender: (record, _index, _indent, expanded) => {
                  if (!expanded) return;
                  const articles = getArticlesById(record.id);
                  const articleResults = getSelectedArticleResults(selectedArticleId, record.id);

                  return (
                    <Descriptions bordered size="small">
                      {record.system_message ? (
                        <Descriptions.Item span={3} label={t("analytics.system_message")}>
                          {record.system_message}
                        </Descriptions.Item>
                      ) : null}

                      {articleResults ? (
                        <Descriptions.Item span={3} label={t("analytics.system_article")}>
                          {articleResults?.article_content}
                        </Descriptions.Item>
                      ) : null}
                      {articles && articles.length ? (
                        <Descriptions.Item span={3} label={t("analytics.articles_list")}>
                          <ul className="pl-4">
                            {Object.values(articles)?.map((article: IArticle) => (
                              <li key={article.article_id}>{article.article_content}</li>
                            ))}
                          </ul>
                        </Descriptions.Item>
                      ) : null}
                      {record.temperature ? (
                        <Descriptions.Item span={2} label={t("analytics.temperature")}>
                          {record.temperature}
                        </Descriptions.Item>
                      ) : null}
                      {record.model ? (
                        <Descriptions.Item span={2} label={t("analytics.model")}>
                          {record.model}
                        </Descriptions.Item>
                      ) : null}
                      {/* TOKENS */}
                      {isScreenBelow("md") && (
                        <>
                          <Descriptions.Item label={t("analytics.cache_hit")}>
                            {record.cache_hit ? <CheckCircleTwoTone /> : <CloseCircleTwoTone />}
                          </Descriptions.Item>
                          <Descriptions.Item label={t("analytics.prompt_tokens")}>
                            {record.cache_hit ? record.hit_prompt_tokens : record.prompt_tokens}
                          </Descriptions.Item>
                          <Descriptions.Item label={t("analytics.completion_tokens")}>
                            {record.cache_hit ? record.hit_completion_tokens : record.completion_tokens}
                          </Descriptions.Item>
                        </>
                      )}
                      {isScreenBelow("lg") && (
                        <Descriptions.Item label={t("analytics.total_tokens")}>
                          {record.cache_hit ? record.hit_completion_tokens + record.hit_prompt_tokens : record.completion_tokens + record.prompt_tokens}
                        </Descriptions.Item>
                      )}
                      {/* ./ TOKENS */}
                      {/* TIME */}
                      {isScreenBelow("md") && (
                        <>
                          <Descriptions.Item label={t("analytics.cache_read_time")}>
                            {record.cache_hit ? record.hit_cache_read_time : record.cache_read_time}
                          </Descriptions.Item>
                          <Descriptions.Item label={t("analytics.processing_time")}>
                            {record.cache_hit ? record.hit_processing_time : record.processing_time}
                          </Descriptions.Item>
                        </>
                      )}
                      {isScreenBelow("lg") && (
                        <Descriptions.Item label={t("analytics.total_time")}>
                          {record.cache_hit ? record.hit_total_time : record.total_time}
                        </Descriptions.Item>
                      )}
                      {/* ./ TIME */}
                      {isScreenBelow("xl") && (
                        <Descriptions.Item label={t("analytics.created_at")}>{getFormattedDate(record.created_at)}</Descriptions.Item>
                      )}
                    </Descriptions>
                  );
                },
                onExpand: (expanded, record) => {
                  if (!expanded) return;
                  setSelectedQnAId(record.id);
                  setSelectedArticleId(record.selected_article_id || record.cache_selected_article_id);
                },
              }}
              dataSource={[...(QnAObject[0].data as IQnA[])].reverse()}
              pagination={{ ...PAGINATION_OPTIONS }}
            />
          ) : null}
        </Card>
      </Col>
    </Row>
  );
};
